/**
 * Signing in, shared by all three portals.
 *
 * One implementation because the rules are the same everywhere and the ways to
 * get them wrong are too: an error message that distinguishes an unknown
 * address from a wrong password, a token in `localStorage`, a second factor
 * treated as an error rather than a step.
 */

export interface Credentials {
  readonly email: string;
  readonly password: string;
  readonly code?: string;
}

export type LoginOutcome =
  /** §3.7 — internal, and no device enrolled yet. Not a refusal: a next step. */
  | { readonly kind: 'enrol'; readonly message: string }
  /** §3.7 — the first of two steps succeeded. Not a failure. */
  | { readonly kind: 'mfa'; readonly message: string }
  | {
      readonly kind: 'signed-in';
      readonly token: string;
      readonly userId: string;
      readonly permissions: readonly string[];
      readonly mustChangePassword: boolean;
      readonly expiresInMinutes: number;
    }
  | { readonly kind: 'refused'; readonly message: string };

/**
 * §43 — one message for every way a sign-in can fail.
 *
 * The API already answers identically; this exists so no screen can improve on
 * it. "That account is locked" is helpful exactly once, to the person who is
 * not supposed to have it.
 */
export const REFUSED_MESSAGE =
  'That email address and password do not match an account.';

export function readLogin(status: number, body: unknown): LoginOutcome {
  const b = (body ?? {}) as Record<string, unknown>;

  if (status === 200 && b['mfa_required'] === true) {
    return { kind: 'mfa', message: String(b['message'] ?? 'Enter your code.') };
  }

  if (status === 200 && typeof b['token'] === 'string') {
    return {
      kind: 'signed-in',
      token: b['token'],
      userId: String(b['user_id'] ?? ''),
      permissions: (b['permissions'] as string[] | undefined) ?? [],
      mustChangePassword: b['must_change_password'] === true,
      expiresInMinutes: Number(b['expires_in_minutes'] ?? 30),
    };
  }

  // §3.7's enrolment answer is a step, not a refusal — the password was right.
  // Distinguishing it tells an attacker nothing they could not learn by trying
  // the account, and treating it as a failure would leave the person stuck at a
  // screen telling them their password is wrong when it is not.
  if (b['code'] === 'MFA_ENROLMENT_REQUIRED' || b['rule_id'] === '3.7') {
    return { kind: 'enrol', message: String(b['message'] ?? 'Set up your authenticator app.') };
  }

  // A rate-limit refusal carries the retry window in the server's message —
  // showing REFUSED_MESSAGE instead would hide the actionable "try again in N
  // minutes" from the person who is legitimately locked out.
  if (status === 429) {
    return {
      kind: 'refused',
      message: String(b['message'] ?? 'Too many attempts. Please wait before trying again.'),
    };
  }

  return { kind: 'refused', message: REFUSED_MESSAGE };
}

/**
 * §3.2 — whether this person may reach a thing.
 *
 * The console builds navigation from the answer. It is not a control: the API
 * checks the same permission again, because a hidden menu item is not a
 * boundary — the endpoint is what a script calls.
 */
export function may(permissions: readonly string[], permission: string): boolean {
  return permissions.includes(permission);
}
